import React from 'react'
import PropTypes from 'prop-types'
import { Tag, Tooltip } from 'antd'
import {
  CHANNEL_COLORS,
  DELIVERY_COLORS,
  PRIORITY_COLORS,
  alertLabel,
  channelLabel,
  deliveryLabel,
  formatAge,
  marginColor,
  partColor,
  partLabel,
  percent,
  statusColor,
  statusLabel
} from './utils'

// The tags the three pages share. Named function components on purpose: an anonymous arrow inside
// an antd `render` trips react/display-name, and these are rendered from column factories.

export function StatusTag({ status }) {
  return <Tag color={statusColor(status)}>{statusLabel(status)}</Tag>
}

StatusTag.propTypes = { status: PropTypes.string }

// `awaiting_funds` carries its own explanation, because the obvious reading of a stalled report is
// that something broke — and nothing did. The money is still ours and a top-up releases it.
export function PartStatusTag({ status }) {
  const tag = <Tag color={partColor(status)}>{partLabel(status)}</Tag>
  if (status !== 'awaiting_funds') return tag
  return (
    <Tooltip title="Nothing failed and nothing was charged. The supplier refused for credit; topping up releases it automatically.">
      {tag}
    </Tooltip>
  )
}

PartStatusTag.propTypes = { status: PropTypes.string }

export function ChannelTag({ channel }) {
  return <Tag color={CHANNEL_COLORS[channel] || 'default'}>{channelLabel(channel)}</Tag>
}

ChannelTag.propTypes = { channel: PropTypes.string }

export function DeliveryTag({ status }) {
  return <Tag color={DELIVERY_COLORS[status] || 'default'}>{deliveryLabel(status)}</Tag>
}

DeliveryTag.propTypes = { status: PropTypes.string }

export function AttentionTag({ kind, priority, since }) {
  if (!kind) return null
  return (
    <Tag color={PRIORITY_COLORS[priority] || 'default'}>
      {alertLabel(kind)} · {formatAge(since)}
    </Tag>
  )
}

AttentionTag.propTypes = {
  kind: PropTypes.string,
  priority: PropTypes.string,
  since: PropTypes.string
}

// Red below the floor, amber near it, green above. The one number on the Products page the owner
// is actually deciding on.
export function MarginTag({ marginPercent, floor }) {
  if (marginPercent === null || marginPercent === undefined || marginPercent === '') {
    return <Tooltip title="No cost known for one of its reports yet."><Tag>—</Tag></Tooltip>
  }
  return <Tag color={marginColor(marginPercent, floor)}>{percent(marginPercent)}</Tag>
}

MarginTag.propTypes = {
  marginPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  floor: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

// On sale, or not. Deliberately plain: every product ships off, so "Off" is the normal state and
// must not read like a fault.
export function ActiveTag({ active }) {
  return <Tag color={active ? 'green' : 'default'}>{active ? 'On sale' : 'Off'}</Tag>
}

ActiveTag.propTypes = { active: PropTypes.bool }
